"use client";

import React, { useState, useEffect, useRef } from "react";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import {
  CheckCircle,
  Calendar,
  Users,
  Award,
  Rocket,
  Code2,
  Zap,
  ShieldCheck,
  Target,
  Settings,
} from "lucide-react";
import davidChangImage from "@assets/images/teams/dbi-analytics-david-patrick-chang-managing-director.png";
import moritzMullerWunschImage from "@assets/images/teams/dbi-analytics-moritz-mueller-wuensch-sales-manger-agentic-bi-tools-consulting-2025.png";
import danielJanzImage from "@assets/images/teams/dbi-analytics-daniel-janz-cto.png";
import artemGoriachevImage from "@assets/images/teams/dbi-analytics-abis-artem-goriachev-head-of-development.png";
import michaelDataArchitectImage from "@assets/images/teams/dbi-analytics-michael-data-architect.png";
import carlGroefkeImage from "@assets/images/teams/dbi-analytics-carl-bi-analyst.png";
import sushantBansalImage from "@assets/images/teams/dbi-analytics-sushant-bansal-fullstack-developer.png";
import christopherMattImage from "@assets/images/teams/dbi-analytics-christopher-matt-senior-frontend-developer-information-designer-freelancer.png";
import samirDashboardDesignImage from "@assets/images/teams/dbi-analytics-samir-dashboard-design.png";
import janaChangImage from "@assets/images/teams/dbi-analytics-jana-chang-operation-assistant.png";

interface TeamMember {
  name: string;
  role: string;
  image: string | null;
}

interface TimelineMilestone {
  id: string;
  date: string;
  title: string;
  description: string;
  teamMembers: TeamMember[];
  achievements: string[];
}

interface TimelineProps {
  milestones: TimelineMilestone[];
  onScrollEnd?: () => void;
  onProgressChange?: (progress: number) => void;
}

// Mapping of team member names to images
// Supports both first name only and full names (for Takeda team members)
const teamImageMap: Record<string, StaticImageData> = {
  David: davidChangImage,
  Moritz: moritzMullerWunschImage,
  Daniel: danielJanzImage,
  Artem: artemGoriachevImage,
  Michael: michaelDataArchitectImage,
  Carl: carlGroefkeImage,
  Sushant: sushantBansalImage,
  Christopher: christopherMattImage,
  Samir: samirDashboardDesignImage,
  Jana: janaChangImage
};

const Timeline: React.FC<TimelineProps> = ({
  milestones,
  onScrollEnd,
  onProgressChange,
}) => {
  const [visibleMilestones, setVisibleMilestones] = useState<Set<string>>(
    new Set()
  );
  const timelineRef = useRef<HTMLDivElement>(null);
  const milestoneRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasTriggeredScrollEnd = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());
  const isMountedRef = useRef(true);
  const scrollEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Ensure we're on the client side
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    isMountedRef.current = true;

    // Cleanup previous observer if exists
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Set up Intersection Observer for milestone animations with smooth entry
    // Optimized for better UX - triggers earlier for smoother feel
    // Use milestone IDs directly in closure to avoid stale dependencies
    const milestoneIds = milestones.map((m) => m.id);

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const milestoneId = entry.target.getAttribute("data-milestone-id");
            if (milestoneId) {
              // Check if already visible to avoid duplicate timeouts
              setVisibleMilestones((prev) => {
                if (prev.has(milestoneId)) {
                  return prev; // Already visible, no need to schedule animation
                }
                // Staggered animation for visual appeal
                const index = milestoneIds.findIndex(
                  (id) => id === milestoneId
                );
                const delay = index >= 0 ? index * 100 : 0; // 100ms delay per milestone
                const timeoutId = setTimeout(() => {
                  if (isMountedRef.current) {
                    setVisibleMilestones((current) => {
                      const updated = new Set(current);
                      updated.add(milestoneId);
                      return updated;
                    });
                  }
                }, delay);
                timeoutRefs.current.add(timeoutId);
                return prev; // Don't update state yet, wait for timeout
              });
            }
          }
        });
      },
      {
        threshold: [0, 0.1, 0.2], // Multiple thresholds for better detection
        rootMargin: "0px 0px -50px 0px", // More generous margin for better trigger
      }
    );

    // Observe all milestone elements - with retry logic for dynamic routes
    const observeMilestones = () => {
      const milestoneElements = document.querySelectorAll(
        "[data-milestone-id]"
      );
      milestoneElements.forEach((el) => {
        if (observerRef.current) {
          observerRef.current.observe(el);
        }
      });
      return milestoneElements.length > 0;
    };

    // Use requestAnimationFrame to ensure DOM is ready - with retry logic for dynamic routes
    let retryCount = 0;
    const maxRetries = 15; // Increased retries for better reliability

    const initObserver = () => {
      requestAnimationFrame(() => {
        const elementsFound = observeMilestones();
        if (!elementsFound && retryCount < maxRetries) {
          retryCount++;
          setTimeout(initObserver, 100);
        } else if (elementsFound) {
          // Force initial check for elements already in viewport - trigger animations immediately
          setTimeout(() => {
            const milestoneElements = document.querySelectorAll(
              "[data-milestone-id]"
            );
            milestoneElements.forEach((el) => {
              const rect = el.getBoundingClientRect();
              const isInViewport =
                rect.top < window.innerHeight * 1.2 && // More generous viewport check
                rect.bottom > -window.innerHeight * 0.2 &&
                rect.left < window.innerWidth &&
                rect.right > 0;

              if (isInViewport && observerRef.current) {
                // Create a synthetic intersection entry to trigger animation
                const milestoneId = el.getAttribute("data-milestone-id");
                if (milestoneId) {
                  setVisibleMilestones((prev) => {
                    if (prev.has(milestoneId)) {
                      return prev; // Already visible, no need to schedule animation
                    }
                    const index = milestoneIds.findIndex(
                      (id) => id === milestoneId
                    );
                    const delay = index >= 0 ? index * 100 : 0;

                    const timeoutId = setTimeout(() => {
                      if (isMountedRef.current) {
                        setVisibleMilestones((current) => {
                          const updated = new Set(current);
                          updated.add(milestoneId);
                          return updated;
                        });
                      }
                    }, delay);
                    timeoutRefs.current.add(timeoutId);
                    return prev; // Don't update state yet, wait for timeout
                  });
                }
              }
            });
          }, 300);
        }
      });
    };

    // Initial delay for dynamic routes to ensure DOM is ready
    const initTimeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        initObserver();
      }
    }, 100);
    timeoutRefs.current.add(initTimeoutId);

    // Modern timeline line scroll animation - smooth, professional, ICP-grade UX
    const updateTimelineProgress = () => {
      if (!timelineRef.current || milestoneRefs.current.size === 0) return;

      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY;

      // Find the first and last milestone positions relative to timeline container
      let firstMilestoneTop = Infinity;
      let lastMilestoneBottom = -Infinity;

      milestoneRefs.current.forEach((element) => {
        const elementRect = element.getBoundingClientRect();
        const elementAbsoluteTop = scrollY + elementRect.top;
        const elementAbsoluteBottom = scrollY + elementRect.bottom;

        if (elementAbsoluteTop < firstMilestoneTop)
          firstMilestoneTop = elementAbsoluteTop;
        if (elementAbsoluteBottom > lastMilestoneBottom)
          lastMilestoneBottom = elementAbsoluteBottom;
      });

      // Calculate timeline bounds with smooth entry/exit - optimized for better sync
      const timelineStart = firstMilestoneTop - windowHeight * 0.4; // Start earlier for smooth sync
      const timelineEnd = lastMilestoneBottom + windowHeight * 0.2; // End with buffer
      const totalTimelineHeight = Math.max(1, timelineEnd - timelineStart);

      // Viewport position for smooth tracking
      const viewportTop = scrollY;
      const viewportBottom = scrollY + windowHeight;

      // Calculate progress with professional easing
      let progress = 0;

      if (viewportTop < timelineStart) {
        // Before timeline - start animation gently
        const earlyProgress =
          ((viewportTop - (timelineStart - windowHeight * 0.4)) /
            (windowHeight * 0.4)) *
          5;
        progress = Math.max(0, Math.min(5, earlyProgress));
      } else if (viewportBottom > timelineEnd) {
        // After timeline - complete
        progress = 100;
      } else {
        // Within timeline - smooth calculation with professional easing
        const scrolled = viewportTop - timelineStart;
        const rawProgress = (scrolled / totalTimelineHeight) * 100;

        // Professional easing function (ease-out-cubic) - smooth, confident finish
        const normalizedProgress = Math.min(1, Math.max(0, rawProgress / 100));
        const easedProgress = 1 - Math.pow(1 - normalizedProgress, 3);

        progress = Math.min(100, Math.max(0, easedProgress * 100));
      }

      // Use requestAnimationFrame for smooth state updates
      const frameId = requestAnimationFrame(() => {
        // Notify parent component of progress change
        if (onProgressChange) {
          onProgressChange(progress);
        }
      });

      // Store animation frame ID for cleanup
      animationFrameRef.current = frameId;
    };

    // Throttled scroll handler for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        const frameId = window.requestAnimationFrame(() => {
          updateTimelineProgress();
          ticking = false;
        });
        animationFrameRef.current = frameId;
        ticking = true;
      }
    };

    // Scroll end detection with debounce
    const handleScrollEnd = () => {
      if (hasTriggeredScrollEnd.current || !timelineRef.current || !isMountedRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const timelineBottom = window.scrollY + rect.bottom;
      const windowBottom = window.scrollY + window.innerHeight;

      // Clear existing timeout
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
        timeoutRefs.current.delete(scrollEndTimeoutRef.current);
      }

      // Debounce scroll end detection
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current && windowBottom >= timelineBottom - 200 && onScrollEnd) {
          hasTriggeredScrollEnd.current = true;
          onScrollEnd();
        }
      }, 150); // Small delay for better UX
      scrollEndTimeoutRef.current = timeoutId;
      timeoutRefs.current.add(timeoutId);
    };

    // Initial calculation after DOM is ready
    const progressTimeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        updateTimelineProgress();
      }
    }, 150);
    timeoutRefs.current.add(progressTimeoutId);

    // Add scroll listeners with passive flag for better performance
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("scroll", handleScrollEnd, { passive: true });

    // Also update on resize
    const handleResize = () => {
      updateTimelineProgress();
    };
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      // Mark as unmounted
      isMountedRef.current = false;

      // Capture current ref values for cleanup
      const currentAnimationFrame = animationFrameRef.current;
      const currentObserver = observerRef.current;

      // Cancel all timeouts
      timeoutRefs.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeoutRefs.current.clear();

      // Cancel animation frame
      if (currentAnimationFrame !== null) {
        cancelAnimationFrame(currentAnimationFrame);
        animationFrameRef.current = null;
      }

      // Remove event listeners
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScrollEnd);
      window.removeEventListener("resize", handleResize);

      // Disconnect observer
      if (currentObserver) {
        currentObserver.disconnect();
        observerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onScrollEnd, milestones]); // onProgressChange intentionally omitted to avoid re-renders

  // ABIS Logo Component for timeline dots
  const AbisLogoSimple = ({
    className = "",
    color = "#1e3a8a",
  }: {
    className?: string;
    color?: string;
  }) => (
    <div className={`relative ${className}`}>
      <svg
        viewBox="0 0 370.66666 416"
        className="w-full h-full"
        shapeRendering="geometricPrecision"
      >
        <g transform="matrix(1.3333333,0,0,-1.3333333,0,416)">
          <g transform="scale(0.1)">
            <path
              style={{
                fill: color,
                fillOpacity: 1,
                fillRule: "nonzero",
                stroke: "none",
              }}
              d="m 1359.26,2777.5 c 105.39,128.48 265.31,247.81 420.9,311.64 99.57,42.31 213.47,39.18 313.98,1.8 153.4,-55.35 288.63,-178.13 406.45,-289.14 -104.73,-136.41 -234.81,-237.5 -394.69,-306.29 -105.47,-46.6 -228.24,-47.19 -334.92,-4.18 -149.38,58.36 -308.6,165.47 -411.72,286.17 z"
            />
            <path
              style={{
                fill: color,
                fillOpacity: 1,
                fillRule: "nonzero",
                stroke: "none",
              }}
              d="M 2770.66,1367.58 C 2770.66,596.449 2120.43,1.60156 1351.95,1.60156 583.52,1.60156 1.76172,620 1.76172,1391.37 c 0,288.75 164.64828,1067.69 972.80828,1311.33 0,0 384.69,-587.58 963.01,-587.5 285.04,0.03 501.95,163.04 501.95,163.04 h 0.67 c 0,0 330.46,-322.81 330.46,-910.66 z"
            />
          </g>
        </g>
      </svg>
    </div>
  );

  // Get icon for milestone based on ID or content
  const getMilestoneIcon = (milestone: TimelineMilestone) => {
    // Map milestone IDs to icons
    const id = milestone.id.toLowerCase();
    const title = milestone.title.toLowerCase();

    if (
      id.includes("kickoff") ||
      id.includes("start") ||
      title.includes("projektstart")
    ) {
      return Rocket;
    }
    if (
      id.includes("reverse") ||
      id.includes("engineering") ||
      title.includes("reverse")
    ) {
      return Code2;
    }
    if (
      id.includes("setup") ||
      id.includes("cicd") ||
      id.includes("wissensaufbau") ||
      title.includes("setup") ||
      title.includes("ci/cd")
    ) {
      return Settings;
    }
    if (
      id.includes("migration") ||
      id.includes("development") ||
      id.includes("pilot") ||
      title.includes("entwicklung")
    ) {
      return Zap;
    }
    if (
      id.includes("testing") ||
      id.includes("validierung") ||
      id.includes("rollout") ||
      title.includes("testing") ||
      title.includes("validierung")
    ) {
      return ShieldCheck;
    }
    if (
      id.includes("go-live") ||
      id.includes("support") ||
      id.includes("validation") ||
      title.includes("go-live")
    ) {
      return Target;
    }

    // Default icon
    return Award;
  };

  const getTeamMemberImage = (member: TeamMember): StaticImageData | null => {
    // Special case: ABIS - return null to trigger ABIS logo display
    if (member.name.toLowerCase() === "abis" || member.name.toLowerCase() === "abislösung") {
      return null; // Will be handled specially in render
    }
    
    // Try exact match first
    if (teamImageMap[member.name] && teamImageMap[member.name] !== null) {
      return teamImageMap[member.name];
    }
    // Try first name only
    const firstName = member.name.split(" ")[0];
    if (teamImageMap[firstName] && teamImageMap[firstName] !== null) {
      return teamImageMap[firstName];
    }
    // No image available (e.g., for external team members like Takeda employees)
    return null;
  };

  return (
    <div
      ref={timelineRef}
      className="w-full relative pb-8"
      aria-label="Projekt Rückblick"
      style={{
        marginBottom: "-60px", // Linie ragt etwas in die nächste Section für verbundenen Look
        position: "relative",
        overflowX: "hidden", // Prevent horizontal scroll on mobile without affecting Intersection Observer
        overflowY: "visible", // Allow tooltips to overflow vertically
      }}
    >
      <div className="relative" style={{ minHeight: "100%", overflow: "visible" }}>
        {/* Timeline Container */}
        <div
          className="relative"
          style={{ height: "100%", paddingBottom: "2rem", overflow: "visible" }}
        >
          {/* Vertical Line - Desktop with static fixed color gradient */}
          <div
            className="absolute left-8 md:left-1/2 top-0 w-0.5 transform -translate-x-1/2 hidden md:block timeline-line-bg z-0"
            style={{
              height: "100%",
              bottom: "0",
              // Static gradient from orange (top) to blue (bottom)
              background: "linear-gradient(to bottom, #fb923c, #fb923c 40%, rgba(251, 146, 60, 0.95) 50%, rgba(251, 146, 60, 0.7) 60%, rgba(30, 58, 138, 0.4) 70%, #1e3a8a 80%, #1e3a8a 100%)",
            }}
          ></div>

          {/* Vertical Line - Mobile */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-abis-primary via-abis-accent to-abis-primary md:hidden"></div>

          {/* Milestones */}
          <div className="space-y-12 md:space-y-16" style={{ overflow: "visible" }}>
            {milestones.map((milestone, index) => {
              const isEven = index % 2 === 0; // Even = right side (0, 2, 4...), Odd = left side (1, 3, 5...)
              const isVisible = visibleMilestones.has(milestone.id);
              
              // Static orange background for all circles
              const iconBackgroundColor = "#fb923c";

              return (
                <div
                  key={milestone.id}
                  ref={(el) => {
                    if (el) {
                      milestoneRefs.current.set(milestone.id, el);
                    } else {
                      milestoneRefs.current.delete(milestone.id);
                    }
                  }}
                  data-milestone-id={milestone.id}
                  className={`relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8 w-full transition-all duration-500 ease-out ${
                    isVisible 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-4'
                  }`}
                >
                  {/* Timeline Dot - Positioned at headline height (h3) - dynamically calculated */}
                  <div
                    className="absolute left-8 md:left-1/2 transform -translate-x-1/2 z-10"
                    style={{
                      // Align with h3 headline: date badge (~40px) + mb-4 (16px) = ~56px from top of card
                      top: "calc(24px + 1rem)", // p-6/p-8 padding + date badge height + mb-4 spacing ≈ h3 position
                    }}
                  >
                    {(() => {
                      // Check if ABIS is mentioned in description or title (but NOT in team members)
                      // ABIS in team members should only appear in the content card, not in timeline dot
                      const title = milestone.title.toLowerCase();
                      const description = milestone.description.toLowerCase();
                      const hasABISInContent =
                        (title.includes("abis") ||
                        description.includes("abis") ||
                        description.includes("<strong>abis</strong>") ||
                        description.includes("abislösung") ||
                        milestone.description.includes("ABIS") ||
                        milestone.title.includes("ABIS")) &&
                        // Only show ABIS logo in dot if NOT in team (team members show in content card)
                        !milestone.teamMembers?.some(
                          (member) => member.name.toLowerCase() === "abis" || member.name.toLowerCase() === "abislösung"
                        );
                      
                      // Get icon based on milestone title/ID
                      const IconComponent = hasABISInContent ? null : getMilestoneIcon(milestone);

                      return (
                        <div className="relative w-8 h-8 md:w-10 md:h-10">
                          {/* Static circle - no animations for performance */}
                          <div
                            className="w-8 h-8 md:w-10 md:h-10 rounded-full border-4 border-abis-accent/30 shadow-lg flex items-center justify-center overflow-hidden relative"
                            style={{
                              backgroundColor: iconBackgroundColor,
                            }}
                          >
                            {/* ABIS Logo or Icon */}
                            {hasABISInContent ? (
                              <Link
                                href="/services/power-bi-mcp-server"
                                className="flex items-center justify-center transition-all duration-500 hover:scale-110 active:scale-95 w-full h-full min-w-[44px] min-h-[44px] focus:outline-none focus:ring-2 focus:ring-abis-accent focus:ring-offset-2 rounded-full"
                                aria-label="ABIS Agentic BI Tool entdecken"
                                title="ABIS - Agentic BI Tool"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <AbisLogoSimple
                                  className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0"
                                  color="#ffffff"
                                />
                              </Link>
                            ) : IconComponent ? (
                              <IconComponent
                                className="w-4 h-4 md:w-5 md:h-5"
                                style={{
                                  color: "#ffffff",
                                }}
                              />
                            ) : null}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Content Card - Exactly 50% width on desktop, alternating left/right, starting from right */}
                  <div
                    className={`ml-16 md:ml-0 w-[calc(100%-4rem)] md:w-full ${
                      isEven
                        ? "md:pr-6 md:text-left md:mr-auto md:w-[calc(50%-24px)] md:max-w-[calc(50%-24px)]"
                        : "md:pl-6 md:text-left md:ml-auto md:w-[calc(50%-24px)] md:max-w-[calc(50%-24px)]"
                    } md:flex-none md:flex-shrink-0 md:flex-grow-0`}
                    style={{ overflow: "visible", position: "relative" }}
                  >
                    <div className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] p-6 md:p-8 border border-gray-100 timeline-milestone-card ${!isEven ? 'md:pl-8' : ''}`}>
                      {/* Date Badge */}
                      <div className="inline-flex items-center px-3 py-1 bg-abis-accent/10 rounded-full border border-abis-accent/20 mb-4">
                        <Calendar className="w-4 h-4 text-abis-accent mr-2" />
                        <span className="text-abis-accent font-semibold text-xs uppercase tracking-wide">
                          {milestone.date}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl md:text-3xl font-bold text-abis-primary mb-3">
                        {milestone.title}
                      </h3>

                      {/* Description */}
                      <p
                        className="text-gray-700 mb-6 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: milestone.description,
                        }}
                      />

                      {/* Team Members */}
                      {milestone.teamMembers &&
                        milestone.teamMembers.length > 0 && (
                          <div className="mb-6" style={{ overflow: 'visible' }}>
                            <div className="flex items-center mb-3">
                              <Users className="w-5 h-5 text-abis-accent mr-2" />
                              <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                Team
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center -space-x-3 md:-space-x-2 relative" style={{ overflow: 'visible', position: 'relative' }}>
                              {milestone.teamMembers.map(
                                (member, memberIndex) => {
                                  const memberImage =
                                    getTeamMemberImage(member);
                                  return (
                                    <div
                                      key={memberIndex}
                                      className="relative group cursor-pointer"
                                      style={{
                                        zIndex: milestone.teamMembers.length -
                                          memberIndex,
                                        overflow: "visible",
                                        position: "relative",
                                      }}
                                      title={`${member.name} - ${member.role}`}
                                    >
                                      {(() => {
                                        // Special case: ABIS - show ABIS logo
                                        const isABIS = member.name.toLowerCase() === "abis" || member.name.toLowerCase() === "abislösung";
                                        
                                        if (isABIS) {
                                          return (
                                            <Link
                              href="/services/power-bi-mcp-server"
                                className={`
                                relative w-12 h-12 md:w-14 md:h-14 lg:w-14 lg:h-14 rounded-full overflow-hidden
                                border-2 md:border-3 border-white transition-all duration-300
                                                bg-abis-accent focus:outline-none focus:ring-2 focus:ring-abis-accent/50 focus:ring-offset-2 focus:ring-offset-white
                                                hover:scale-110 hover:shadow-lg shadow-md hover:-translate-y-1
                                                flex items-center justify-center
                                                ${
                                                  memberIndex === 0
                                                    ? ""
                                                    : "-ml-3 md:-ml-2"
                                                }
                                              `}
                                              title="ABIS - Agentic BI Tool"
                                              aria-label="ABIS Agentic BI Tool entdecken"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <AbisLogoSimple
                                                className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0"
                                                color="#1e3a8a"
                                              />
                                            </Link>
                                          );
                                        }
                                        
                                        // Regular team member with image
                                        if (memberImage) {
                                          return (
                                            <div
                                              className={`
                                                relative w-12 h-12 md:w-14 md:h-14 lg:w-14 lg:h-14 rounded-full overflow-hidden
                                                border-2 md:border-3 border-white transition-all duration-300
                                                bg-abis-accent focus:outline-none focus:ring-2 focus:ring-abis-accent/50 focus:ring-offset-2 focus:ring-offset-white
                                                hover:scale-110 hover:shadow-lg shadow-md hover:-translate-y-1
                                                ${
                                                  memberIndex === 0
                                                    ? ""
                                                    : "-ml-3 md:-ml-2"
                                                }
                                              `}
                                            >
                                              <Image
                                                src={memberImage}
                                                alt={`${member.name}, ${member.role}`}
                                                fill
                                                className="object-cover"
                                                sizes="56px"
                                              />
                                            </div>
                                          );
                                        }
                                        
                                        // Fallback: Initials
                                        return (
                                          <div
                                            className={`
                                              w-12 h-12 md:w-14 md:h-14 lg:w-14 lg:h-14 rounded-full bg-abis-accent 
                                              border-2 md:border-3 border-white shadow-md flex items-center justify-center
                                              transition-all duration-300 hover:scale-110 hover:shadow-lg hover:-translate-y-1
                                              ${
                                                memberIndex === 0
                                                  ? ""
                                                  : "-ml-3 md:-ml-2"
                                              }
                                            `}
                                          >
                                            <span className="text-white font-semibold text-sm">
                                              {member.name.charAt(0)}
                                            </span>
                                          </div>
                                        );
                                      })()}
                                      {/* Tooltip - Links ausgerichtet über den Buttons, wie Homepage Team-Member */}
                                      <div className="absolute bottom-full left-0 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-[9998]">
                                        <div className="bg-white text-gray-900 text-xs rounded-lg px-3 py-2 shadow-lg border border-gray-200 relative">
                                          <div className="font-semibold text-gray-900">
                                            {member.name}
                                          </div>
                                          <div className="text-gray-600">
                                            {member.role}
                                          </div>
                                          {/* Pfeil links unten im Popover, zeigt auf Avatar-Mitte */}
                                          <div className="absolute top-full left-4 -mt-px w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        )}

                      {/* Achievements */}
                      {milestone.achievements &&
                        milestone.achievements.length > 0 && (
                          <div>
                            <ul className="space-y-2">
                              {milestone.achievements.map(
                                (achievement, achievementIndex) => (
                                  <li
                                    key={achievementIndex}
                                    className="flex items-start text-gray-700"
                                  >
                                    <CheckCircle className="w-5 h-5 text-abis-success mr-2 flex-shrink-0 mt-0.5" />
                                    <span
                                      className="text-sm leading-relaxed"
                                      dangerouslySetInnerHTML={{
                                        __html: achievement,
                                      }}
                                    />
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Spacer for mobile */}
                  <div className="md:hidden w-full h-0"></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Timeline;
